import { useId } from '@reach/auto-id';
import classnames from 'classnames2';
import PopperJS from 'popper.js';
import React from 'react';

import { IBaseProps } from '../utils';

import { Icon } from '../Icon';
import { LoadingSpinner } from '../LoadingSpinner';

import { isSelectFooter } from './Select.Footer';
import { SelectList } from './Select.List';
import styles from './Select.module.scss';
import { SelectContextWrapper } from './SelectContextWrapper';
import { EChildMenuDirections } from './useControls';
import { useSelectContext } from './useSelectContext';

import { OnChangeHandler, SelectIsEqual, SelectWidths } from './types';

export interface ISelectProps<T = any> extends IBaseProps {
  /** Set to true if this checkbox should be disabled.
   *  The user won’t be able to trigger a onChange callback when clicking it
   */
  disabled?: boolean;
  id?: string;
  /** Label id for aria-labelledby */
  labelId?: string;
  /** Determines if button should be displayed in loading state. Used when options in menu are being populated */
  loading?: boolean;
  /** Message displayed during loading state next to spinner. Should end in an ellipsis ("..."). */
  loadingText?: string;
  /** Maximum height for menu */
  maxMenuHeight?: number | string;
  /** Placeholder text for Select */
  placeholder?: string;
  /** Component to be placed before Select */
  prefix?: React.ReactElement;
  /** Component to be placed after Select */
  suffix?: React.ReactElement;
  /** The form value associated with this option. It is passed to the onChange callback on Select. */
  value?: any;
  width?: SelectWidths;
  size?: 'Default' | 'Small';
  /** The placment of the popover */
  placement?: PopperJS.Placement;
  /** @deprecated the label is now handled internally via the option label
   *  and this will be removed in next major version
   */
  valueLabel?: string;
  /** Whether or not to automatically flip the menu if it goes off screen */
  flip?: boolean;
  /** Whether to set the popper menu to position fixed (or absolute) - default is true */
  fixed?: boolean;
  isEqual?: SelectIsEqual;
  /** Where to display the child menu(s) */
  childDirection?: EChildMenuDirections;
  buttonAriaLabel?: string;
  /** If set to true, will only display a selected child name, rather than including the parent. */
  omitSelectedParentLabel?: boolean;
  /** A function that is called whenever the user tries to update the checkbox input control value,
   *  except when disabled is set to true
   */
  onChange?: OnChangeHandler<T>;
  onBlur?(event: React.FocusEvent<HTMLInputElement>): void;
  onClick?(event: React.MouseEvent<HTMLButtonElement>): void;
}

const SelectButton = (props) => {
  const { size = 'Default', onClick } = props;

  const { isOpen, menuId, buttonId, buttonRef, hasParentMenu, onClose, openAndFocusMenu } = useSelectContext();

  const handleOnClick = React.useCallback(() => {
    // Prevent this action if it's not top-level button
    if (hasParentMenu) {
      return;
    }

    if (isOpen) {
      onClose();
    }

    if (!isOpen) {
      openAndFocusMenu();
    }
  }, [hasParentMenu, isOpen]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        openAndFocusMenu();
      }
    },
    [openAndFocusMenu]
  );

  const buttonWidthStyle = () => {
    switch (props.width) {
      case 'full':
        return styles.fullContainerWidth;
      case 'min':
        return null;
      default:
        return styles.defaultButtonWidth;
    }
  };

  const buttonSizeStyle = {
    [styles.defaultButtonSize]: size === 'Default',
    [styles.smallButtonSize]: size === 'Small'
  };

  const labelledbyId = `button-value-${useId()}`;

  return (
    <button
      aria-labelledby={`${labelledbyId} ${props.labelId}`}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-controls={menuId}
      id={props.id ? props.id : buttonId}
      ref={buttonRef}
      className={classnames(styles.button, buttonSizeStyle, buttonWidthStyle())}
      data-test={props.dataTest}
      onClick={onClick ? onClick : handleOnClick}
      onKeyDown={handleKeyDown}
      disabled={props.disabled || props.loading}
      type="button"
    >
      {props.loading ? (
        <LoadingSpinner message={props.loadingText} />
      ) : (
        <span id={labelledbyId} className={styles.buttonLabel}>
          {props.label}
        </span>
      )}
      {props.prefixIcon && <div className={styles.iconWrapper}>{props.prefixIcon}</div>}
      <Icon icon="arrow-caret-down" className={styles.caretIcon} />
    </button>
  );
};

const containerStyle = (width) => {
  switch (width) {
    case 'full':
      return styles.flexGrow;
    default:
      return null;
  }
};

function collectOptions(childrenOptions) {
  if (!childrenOptions) return [];

  return React.Children.toArray(childrenOptions)
    .map((child) => {
      if (child?.type.displayName === 'Select.Option') {
        return [child.props, collectOptions(child.props.children)].flat();
      }
    })
    .flat();
}

export const Select: React.FC<ISelectProps> = (props) => {
  const { className, width, children, maxMenuHeight, onBlur } = props;
  const withFooter = isSelectFooter(React.Children.toArray(children).pop() as React.SFCElement<any>);
  const styleForContainer = containerStyle(props.width);

  const options = collectOptions(props.children).flat().filter(Boolean);

  const selectedOptionLabel = options.find((option) => option.value === props.value)?.label;

  return (
    <SelectContextWrapper
      onChange={props.onChange}
      selectedValue={props.value}
      flip={props.flip}
      childDirection={props.childDirection}
      placement={props.placement}
      width={width}
      fixed={props.fixed}
    >
      <div className={classnames(styles.flexContainer, className)}>
        <div className={classnames(styles.container, styleForContainer)}>
          <SelectButton
            size={props.size}
            width={props.width}
            labelId={props.labelId}
            label={props.valueLabel ?? selectedOptionLabel ?? props.placeholder}
            dataTest={props.dataTest}
            disabled={props.disabled}
            loading={props.loading}
            loadingText={props.loadingText}
            id={props.id}
          />
          <SelectList onBlur={onBlur} width={width} height={maxMenuHeight} hasFooter={withFooter}>
            {props.children}
          </SelectList>
        </div>
      </div>
    </SelectContextWrapper>
  );
};

Select.displayName = 'Select';
